import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { X, Plus, Calendar, Clock, Eye, Target, MessageSquare, Upload, Image as ImageIcon } from 'lucide-react';

const PopupForm = ({ popup, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    enabled: true,
    priority: 0,
    startDate: '',
    endDate: '',
    dailySchedule: {
      enabled: false,
      startTime: '09:00',
      endTime: '18:00'
    },
    targetAudience: 'all',
    backgroundType: 'color',
    backgroundColor: '#ffffff',
    backgroundImage: null,
    displayDuration: 0,
    closable: true,
    delaySeconds: 0,
    ctas: [{
      text: '',
      url: '',
      primary: true
    }]
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState('');

  useEffect(() => {
    if (popup) {
      setFormData({
        title: popup.title || '',
        description: popup.description || '',
        content: popup.content || '',
        enabled: popup.enabled !== undefined ? popup.enabled : true,
        priority: popup.priority || 0,
        startDate: popup.startDate ? new Date(popup.startDate).toISOString().split('T')[0] : '',
        endDate: popup.endDate ? new Date(popup.endDate).toISOString().split('T')[0] : '',
        dailySchedule: popup.dailySchedule || {
          enabled: false,
          startTime: '09:00',
          endTime: '18:00'
        },
        targetAudience: popup.targetAudience || 'all',
        backgroundType: popup.backgroundType || 'color',
        backgroundColor: popup.backgroundColor || '#ffffff',
        backgroundImage: popup.backgroundImage || null,
        displayDuration: popup.displayDuration || 0,
        closable: popup.closable !== undefined ? popup.closable : true,
        delaySeconds: popup.delaySeconds || 0,
        ctas: popup.ctas && popup.ctas.length > 0 ? popup.ctas.map(cta => ({
          text: cta.text || '',
          url: cta.url || '',
          primary: cta.primary || false
        })) : [{
          text: '',
          url: '',
          primary: true
        }]
      });
      if (popup.backgroundImage) {
        setBackgroundImagePreview(popup.backgroundImage);
      }
    }
  }, [popup]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value) : value)
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setBackgroundImagePreview(reader.result);
      setFormData(prev => ({ ...prev, backgroundImage: file }));
    };
    reader.readAsDataURL(file);
  };

  const handleCTAChange = (index, field, value) => {
    const newCTAs = [...formData.ctas];
    newCTAs[index][field] = value;
    setFormData(prev => ({ ...prev, ctas: newCTAs }));
  };

  const addCTA = () => {
    setFormData(prev => ({
      ...prev,
      ctas: [...prev.ctas, { text: '', url: '', primary: false }]
    }));
  };

  const removeCTA = (index) => {
    if (formData.ctas.length > 1) {
      setFormData(prev => ({
        ...prev,
        ctas: prev.ctas.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // Convert form data to proper types before submitting
      const submitData = {
        ...formData,
        priority: parseInt(formData.priority),
        displayDuration: parseInt(formData.displayDuration),
        delaySeconds: parseInt(formData.delaySeconds),
        startDate: new Date(formData.startDate),
        endDate: new Date(formData.endDate),
        // Ensure boolean fields are properly set
        enabled: Boolean(formData.enabled),
        closable: Boolean(formData.closable),
        dailySchedule: {
          ...formData.dailySchedule,
          enabled: Boolean(formData.dailySchedule.enabled)
        },
        // Map CTAs with proper types
        ctas: formData.ctas.map(cta => ({
          ...cta,
          primary: Boolean(cta.primary)
        }))
      };

      // If it's an edit, include the ID
      if (popup) {
        submitData._id = popup._id;
      }

      // Create FormData for file upload
      const formDataToSend = new FormData();
      
      // Append all fields except the file
      Object.keys(submitData).forEach(key => {
        if (key === 'backgroundImage') {
          // Skip the backgroundImage field for now
          return;
        } else if (key === 'dailySchedule') {
          // Handle nested dailySchedule object
          formDataToSend.append('dailySchedule[enabled]', submitData.dailySchedule.enabled);
          formDataToSend.append('dailySchedule[startTime]', submitData.dailySchedule.startTime);
          formDataToSend.append('dailySchedule[endTime]', submitData.dailySchedule.endTime);
        } else if (key === 'ctas') {
          // Handle CTAs array
          submitData.ctas.forEach((cta, index) => {
            formDataToSend.append(`ctas[${index}][text]`, cta.text);
            formDataToSend.append(`ctas[${index}][url]`, cta.url);
            formDataToSend.append(`ctas[${index}][primary]`, cta.primary);
          });
        } else {
          // Handle other fields
          formDataToSend.append(key, submitData[key]);
        }
      });

      // Append the backgroundImage - either as file or existing URL
      if (formData.backgroundImage && formData.backgroundImage instanceof File) {
        formDataToSend.append('backgroundImage', formData.backgroundImage);
      } else if (submitData.backgroundImage && typeof submitData.backgroundImage === 'string') {
        formDataToSend.append('backgroundImage', submitData.backgroundImage);
      }

      await onSubmit(formDataToSend);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(error.message || 'Failed to save popup');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <MessageSquare className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {popup ? 'Edit Popup' : 'Create New Popup'}
                </h2>
                <p className="text-gray-600 text-sm">
                  {popup ? 'Update popup details and settings' : 'Create a promotional popup for your users'}
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <MessageSquare className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter popup title"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Brief description of the popup"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Detailed content for the popup"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority (0-100)
                  </label>
                  <input
                    type="number"
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Audience
                  </label>
                  <select
                    name="targetAudience"
                    value={formData.targetAudience}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Users</option>
                    <option value="new">New Users</option>
                    <option value="returning">Returning Users</option>
                    <option value="premium">Premium Users</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">Enable popup</label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="closable"
                  checked={formData.closable}
                  onChange={handleChange}
                  className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <label className="text-sm font-medium text-gray-700">Allow users to close popup</label>
              </div>
            </div>

            {/* Background */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <ImageIcon className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Background</h3>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Background Type
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="backgroundType"
                        value="color"
                        checked={formData.backgroundType === 'color'}
                        onChange={handleChange}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Solid Color</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="backgroundType"
                        value="image"
                        checked={formData.backgroundType === 'image'}
                        onChange={handleChange}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Background Image</span>
                    </label>
                  </div>
                </div>

                {formData.backgroundType === 'color' ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Background Color
                    </label>
                    <input
                      type="color"
                      name="backgroundColor"
                      value={formData.backgroundColor}
                      onChange={handleChange}
                      className="w-20 h-10 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Background Image
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-20 w-20 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 overflow-hidden">
                          {backgroundImagePreview ? (
                            <img 
                              src={backgroundImagePreview} 
                              alt="Preview" 
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Upload className="h-8 w-8 text-gray-400" />
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="flex">
                          <label className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500">
                            Choose File
                            <input 
                              type="file" 
                              className="sr-only" 
                              onChange={handleFileChange}
                              accept="image/*"
                            />
                          </label>
                          {backgroundImagePreview && (
                            <button
                              type="button"
                              onClick={() => {
                                setBackgroundImagePreview('');
                                setFormData(prev => ({ ...prev, backgroundImage: null }));
                              }}
                              className="ml-2 p-2 text-gray-500 hover:text-gray-700"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </div>
                        <p className="mt-1 text-xs text-gray-500">
                          PNG, JPG, GIF up to 5MB
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Schedule Settings */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Schedule Settings</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    name="dailySchedule.enabled"
                    checked={formData.dailySchedule.enabled}
                    onChange={handleChange}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <label className="text-sm font-medium text-gray-700">Enable Daily Schedule</label>
                </div>

                {formData.dailySchedule.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      <input
                        type="time"
                        name="dailySchedule.startTime"
                        value={formData.dailySchedule.startTime}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      <input
                        type="time"
                        name="dailySchedule.endTime"
                        value={formData.dailySchedule.endTime}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Display Settings */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Eye className="h-5 w-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Display Settings</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Duration (seconds)
                  </label>
                  <input
                    type="number"
                    name="displayDuration"
                    value={formData.displayDuration}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="0 for indefinite"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Delay (seconds)
                  </label>
                  <input
                    type="number"
                    name="delaySeconds"
                    value={formData.delaySeconds}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Delay before showing popup"
                  />
                </div>
              </div>
            </div>

            {/* Call-to-Action Buttons */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Target className="h-5 w-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Call-to-Action Buttons</h3>
                </div>
                <button
                  type="button"
                  onClick={addCTA}
                  className="flex items-center space-x-1 px-3 py-1 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <Plus size={16} />
                  <span>Add CTA</span>
                </button>
              </div>

              <div className="space-y-3">
                {formData.ctas.map((cta, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-gray-900">Button {index + 1}</h4>
                      {formData.ctas.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeCTA(index)}
                          className="p-1 text-red-500 hover:text-red-700 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Button Text
                        </label>
                        <input
                          type="text"
                          value={cta.text}
                          onChange={(e) => handleCTAChange(index, 'text', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="Learn More"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Button URL
                        </label>
                        <input
                          type="url"
                          value={cta.url}
                          onChange={(e) => handleCTAChange(index, 'url', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          placeholder="https://example.com"
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={cta.primary}
                        onChange={(e) => handleCTAChange(index, 'primary', e.target.checked)}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label className="text-sm font-medium text-gray-700">Primary Button</label>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    {popup ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Eye size={16} />
                    {popup ? 'Update Popup' : 'Create Popup'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PopupForm;